import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon, UserGroupIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import TestimonialCard from '../components/TestimonialCard';
import TestimonialModal from '../components/TestimonialModal';
import ConfirmDialog from '../components/ConfirmDialog';

const TestimonialsPage = () => {
    const [activeTab, setActiveTab] = useState('list');
    const [testimonials, setTestimonials] = useState([]);
    const [settings, setSettings] = useState({
        section_title: '',
        section_subtitle: ''
    });
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingTestimonial, setEditingTestimonial] = useState(null);
    const [savingSettings, setSavingSettings] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, testimonialId: null });

    // Fetch testimonials
    const fetchTestimonials = async () => {
        try {
            const response = await axios.get('/api/admin/testimonials');
            setTestimonials(response.data.data || []);
        } catch (error) {
            console.error('Error fetching testimonials:', error);
            toast.error('Failed to load testimonials');
        }
    };

    // Fetch settings
    const fetchSettings = async () => {
        try {
            const response = await axios.get('/api/testimonials/settings');
            if (response.data.data) {
                setSettings(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
        }
    };

    const fetchAll = async () => {
        setLoading(true);
        await Promise.all([fetchTestimonials(), fetchSettings()]);
        setLoading(false);
    };

    useEffect(() => {
        fetchAll();
    }, []);

    const handleAddNew = () => {
        setEditingTestimonial(null);
        setIsModalOpen(true);
    };

    const handleEdit = (testimonial) => {
        setEditingTestimonial(testimonial);
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        setDeleteConfirm({ isOpen: true, testimonialId: id });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/admin/testimonials/${deleteConfirm.testimonialId}`);
            toast.success('Testimonial deleted successfully');
            fetchTestimonials();
        } catch (error) {
            console.error('Error deleting testimonial:', error);
            toast.error('Failed to delete testimonial');
        }
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setEditingTestimonial(null);
    };

    const handleSave = () => {
        handleModalClose();
        fetchTestimonials();
    };

    const handleSettingsChange = (field, value) => {
        setSettings(prev => ({ ...prev, [field]: value }));
    };

    const handleSettingsSave = async () => {
        try {
            setSavingSettings(true);
            await axios.put('/api/admin/testimonials/settings', settings);
            toast.success('Settings updated successfully');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to save settings');
        } finally {
            setSavingSettings(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Testimonials</h1>
                    <p className="text-slate-500 mt-1">Manage customer stories and reviews</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-1.5 inline-flex gap-1">
                <button
                    onClick={() => setActiveTab('list')}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${activeTab === 'list'
                        ? 'bg-primary-600 text-white shadow-lg'
                        : 'text-slate-600 hover:bg-slate-50'
                        }`}
                >
                    <UserGroupIcon className="h-5 w-5" />
                    Reviews ({testimonials.length})
                </button>
                <button
                    onClick={() => setActiveTab('settings')}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${activeTab === 'settings'
                        ? 'bg-primary-600 text-white shadow-lg'
                        : 'text-slate-600 hover:bg-slate-50'
                        }`}
                >
                    <Cog6ToothIcon className="h-5 w-5" />
                    Settings
                </button>
            </div>

            {/* List Tab */}
            {activeTab === 'list' && (
                <div className="space-y-6">
                    <div className="flex justify-end">
                        <button
                            onClick={handleAddNew}
                            className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl"
                        >
                            <PlusIcon className="h-5 w-5" />
                            Add Testimonial
                        </button>
                    </div>

                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="bg-white rounded-2xl h-64 animate-pulse border border-slate-100"></div>
                            ))}
                        </div>
                    ) : testimonials.length === 0 ? (
                        <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-slate-100">
                            <PlusIcon className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-slate-800">No testimonials yet</h3>
                            <p className="text-slate-500 mb-6">Start by adding your first customer review</p>
                            <button
                                onClick={handleAddNew}
                                className="bg-primary-600 text-white px-6 py-3 rounded-xl font-semibold"
                            >
                                Add First Testimonial
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {testimonials.map((t) => (
                                <TestimonialCard
                                    key={t.id}
                                    testimonial={t}
                                    onEdit={handleEdit}
                                    onDelete={handleDelete}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
                    <h2 className="text-xl font-bold text-slate-800 mb-6">Section Settings</h2>
                    <div className="space-y-6 max-w-2xl">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Section Title</label>
                            <input
                                type="text"
                                value={settings.section_title}
                                onChange={(e) => handleSettingsChange('section_title', e.target.value)}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                placeholder="What Our Customers Say"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Section Subtitle</label>
                            <textarea
                                value={settings.section_subtitle}
                                onChange={(e) => handleSettingsChange('section_subtitle', e.target.value)}
                                rows={3}
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none resize-none"
                                placeholder="Real experiences from satisfied customers"
                            />
                        </div>
                        <div className="pt-4">
                            <button
                                onClick={handleSettingsSave}
                                disabled={savingSettings}
                                className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-xl font-semibold transition-all disabled:opacity-50"
                            >
                                {savingSettings ? 'Saving...' : 'Save Settings'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal */}
            {isModalOpen && (
                <TestimonialModal
                    testimonial={editingTestimonial}
                    onClose={handleModalClose}
                    onSave={handleSave}
                />
            )}

            {/* Delete Confirm Dialog */}
            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, testimonialId: null })}
                onConfirm={confirmDelete}
                title="Delete Testimonial"
                message="Are you sure you want to delete this testimonial? This action cannot be undone."
            />
        </div>
    );
};

export default TestimonialsPage;
